import * as uuid from 'uuid';
import { chunk } from 'lodash';
import { createAdditionalLines } from '../orderlines/createAdditionalLines';
import { generateDiscountLine } from '../orderlines/generateDiscountLine';
import OrderLineOptionsGetter from '../orderlines/orderlineOptionsGetter';
import { reduceRight, reduceRightWithParent } from './OrderHelpers';
import * as OrderLineMappingG5Helper from './OrderLineMappingHelper';
import { evaluatePriceRules, getEffectiveQuantityDiscountLimit, shouldApplyDiscount } from './OrderRuleEvaluator';
import { ensureOrderBuildSession } from './OrderBuildSession';
import { DomainCoreGeneration } from '../libraries/DomainCoreGeneration';
import { PriceMgr } from './PriceMgr';
import { extractValueFromOrderTags } from './OrderTags';
import { DiscountScope } from '../plugins/commonTypes/ProductTypes';
import { CUSTOMER_TYPES } from '../plugins/commonTypes/CommonTypes';
import { calculateVat } from '../vatRates/VatRatesMgr';
import { isGiveawayCampaignDiscountOption, isProductSpecificDiscount } from '../priceRules/priceRulesHelpers';
import { getOrderLineDiscountValue, getOrderLinePrice, orderLineSetSkipInTotalRabat, } from '../orderlines/orderlineHelper';
const assignVirtualIds = (orderLines) => {
    reduceRight(orderLines, (acc, curr) => {
        curr.virtualId = uuid.v4();
        return null;
    }, null);
};
const injectIncludedPrices = (orderLines, includedPriceDisabled) => {
    const getIncludedPrice = (curr) => {
        const includedPrice = curr.options &&
            curr.options.foreignIds &&
            curr.options.foreignIds.onlinePosRules &&
            curr.options.foreignIds.onlinePosRules.cart &&
            curr.options.foreignIds.onlinePosRules.cart.find((c) => c.inclPrice);
        return includedPrice;
    };
    reduceRight(orderLines, (acc, curr) => {
        const includedPrice = includedPriceDisabled ? false : getIncludedPrice(curr);
        if (includedPrice && curr.type === 'Product') {
            const myQuantity = 1;
            const totalPrice = myQuantity * includedPrice.inclPrice;
            const totalVat = calculateVat(curr.vatRatePerMille, totalPrice);
            curr.subLines.push(Object.assign(Object.assign({ quantity: myQuantity, price: includedPrice.inclPrice, totalPrice, subLines: [], accounting: undefined, aggregatedQuantity: undefined, discount: undefined, applicableDiscounts: undefined, generation: '', options: undefined, productId: undefined, productSelectionId: undefined, totalPriceAccum: 0, type: 'Product', unitType: undefined, name: includedPrice.name, vatRatePerMille: curr.vatRatePerMille, totalVat }, (curr.vatCategoryId ? { vatCategoryId: curr.vatCategoryId } : {})), (curr.vatCategoryName ? { vatCategoryName: curr.vatCategoryName } : {})));
        }
        return acc;
    }, {});
};
const applyCombinatorDiscounts = (topLevelOrderLines, rulesToApply) => {
    const productIdsWithHigherQuantity = [];
    const ungroupedLines = topLevelOrderLines.reduce((acc, line) => {
        if (line.type === 'Product') {
            if (line.quantity) {
                const entries = [];
                if (line.quantity > 1) {
                    productIdsWithHigherQuantity.push(line.productId);
                }
                for (let i = 0; i < line.quantity; i++) {
                    entries.push(Object.assign(Object.assign({}, line), { quantity: 1, totalPrice: line.price || line.totalPrice }));
                }
                return acc.concat(entries);
            }
        }
        return acc.concat(line);
    }, []);
    const createCombinatorLine = (newCombinedPrice) => (xs) => {
        return xs.reduce((acc, curr) => {
            OrderLineMappingG5Helper.accumulatePrices([curr]);
            const additionalExpenses = reduceRightWithParent([curr], (memo, node, parent) => {
                if (node.type === 'Aggregation') {
                    if (node.options.includedInMenuPrice) {
                        const diff = node.totalPriceAccum - node.options.includedInMenuPrice;
                        return memo + diff;
                    }
                    if (parent &&
                        'totalPrice' in parent &&
                        'totalPriceAccum' in parent &&
                        (parent.totalPriceAccum === parent.totalPrice || parent.totalPrice === 0)) {
                        return memo + (node.totalPriceAccum || 0);
                    }
                }
                return memo;
            }, 0, null);
            return Object.assign(Object.assign({}, acc), { name: acc.name === '' ? curr.name : `${acc.name} & ${curr.name}`, subLines: acc.subLines.concat(curr), originalTotalPriceAccum: acc.originalTotalPriceAccum + (curr.type === 'Product' ? curr.totalPriceAccum || 0 : 0), totalPriceAccum: acc.totalPriceAccum + additionalExpenses, productIds: acc.productIds.concat(curr.type === 'Product' ? curr.productId : '') });
        }, {
            name: '',
            options: {},
            subLines: [],
            type: 'CombinatorLine',
            generation: xs[0].generation,
            totalPriceAccum: newCombinedPrice,
            totalPrice: newCombinedPrice,
            originalTotalPriceAccum: 0,
            productIds: [],
            quantity: 1,
        });
    };
    const application = rulesToApply.reduce((acc, rule) => {
        const { matched, unmatched } = acc.topLevelOrderLines.reduce((innerAcc, line) => {
            if (line.subLines && line.subLines.length > 0) {
                const secondLevel = applyCombinatorDiscounts(line.subLines, rulesToApply);
                if (line.type !== 'CombinatorLine' ||
                    (secondLevel[0].length === 0 && secondLevel[1].every((d) => d.type !== 'CombinatorLine'))) {
                    line.subLines = [].concat(...secondLevel);
                }
            }
            if (line.type === 'Product' && rule.productPattern.includes(line.productId)) {
                return {
                    matched: innerAcc.matched.concat(line),
                    unmatched: innerAcc.unmatched,
                };
            }
            return {
                matched: innerAcc.matched,
                unmatched: innerAcc.unmatched.concat(line),
            };
        }, { matched: [], unmatched: [] });
        const remainder = matched.length % rule.options.applyQuantity;
        const splitIndex = matched.length - remainder;
        const [groups, remainingLines] = [matched.slice(0, splitIndex), matched.slice(splitIndex)];
        const newCombinedLines = chunk(groups, rule.options.applyQuantity).map(createCombinatorLine(rule.options.newCombinedPrice));
        const rebuilt = remainingLines.concat(unmatched).reduce((lineAcc, current) => {
            if (current.type !== 'Product') {
                return lineAcc.concat(current);
            }
            const existing = lineAcc.find((candidate) => candidate.type === 'Product' &&
                candidate.productId === current.productId &&
                productIdsWithHigherQuantity.includes(candidate.productId));
            if (existing) {
                const differentRuleExists = rulesToApply.find((secondRule) => secondRule.discountId !== rule.discountId && secondRule.productPattern.includes(current.productId));
                if (!differentRuleExists) {
                    return lineAcc.map((candidate) => {
                        if (candidate === existing) {
                            return Object.assign(Object.assign({}, candidate), { quantity: (candidate.quantity || 0) + 1, totalPrice: (candidate.totalPrice || 0) + (current.totalPrice || 0) });
                        }
                        return candidate;
                    });
                }
            }
            return lineAcc.concat(current);
        }, []);
        return {
            topLevelOrderLines: rebuilt,
            combinedLines: acc.combinedLines.concat(newCombinedLines),
        };
    }, { topLevelOrderLines: ungroupedLines, combinedLines: [] });
    return [application.combinedLines, application.topLevelOrderLines];
};
const findBestApplicableDiscount = (applicableDiscounts, options, tempComputedPrice, isMenu) => {
    if (!applicableDiscounts || applicableDiscounts.length === 0) {
        return null;
    }
    const voucherFiltered = applicableDiscounts.filter((discount) => {
        var _a;
        const discountScope = ((_a = discount === null || discount === void 0 ? void 0 : discount.options) === null || _a === void 0 ? void 0 : _a.discountScope) || DiscountScope.All;
        const discountScopes = isMenu
            ? [DiscountScope.Group, DiscountScope.All]
            : [DiscountScope.Single, DiscountScope.All];
        const isDiscountApplicable = discountScopes.includes(discountScope);
        return isDiscountApplicable && shouldApplyDiscount(discount, options);
    });
    if (voucherFiltered.length === 0) {
        return null;
    }
    return voucherFiltered
        .map((discount) => (Object.assign(Object.assign({}, discount), { discountedPrice: PriceMgr.compute(Object.assign(Object.assign({}, tempComputedPrice), { isMenu })) })))
        .reduce((prev, curr) => {
        if ('discountedPrice' in prev && prev.discountedPrice > curr.discountedPrice) {
            return prev;
        }
        return curr;
    });
};
const applyMenuSubProductDiscountCap = ({ computedResult, linePrice, quantity, discount, rootOrderLine, parentOrderLine, productId, discountedQty, vatRatePerMille, }) => {
    var _a, _b, _c;
    if (!((_a = discount === null || discount === void 0 ? void 0 : discount.productPattern) === null || _a === void 0 ? void 0 : _a.includes(rootOrderLine.productId))) {
        return computedResult;
    }
    if (!discount || !Number.isFinite(linePrice) || !Number.isFinite(quantity) || !(rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.isMenu)) {
        return computedResult;
    }
    if (!((_b = parentOrderLine === null || parentOrderLine === void 0 ? void 0 : parentOrderLine.options) === null || _b === void 0 ? void 0 : _b.defaultSelected) || parentOrderLine.options.defaultSelected === productId) {
        return computedResult;
    }
    const includedInMenuPrice = parentOrderLine.options.includedInMenuPrice;
    if (!Number.isFinite(includedInMenuPrice)) {
        return computedResult;
    }
    if (discount.discountType !== 'simpleDiscountFixedAmount' && discount.discountType !== 'percentDiscount') {
        return computedResult;
    }
    const defaultDiscounted = PriceMgr.compute({
        price: includedInMenuPrice,
        quantity: 1,
        discount,
        isMenu: rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.isMenu,
    });
    const defaultDiscountedPrice = (_c = defaultDiscounted.price) !== null && _c !== void 0 ? _c : defaultDiscounted.totalPrice;
    if (!Number.isFinite(defaultDiscountedPrice)) {
        return computedResult;
    }
    const discountAmount = Math.max(includedInMenuPrice - defaultDiscountedPrice, 0);
    const cappedUnitPrice = Math.max(linePrice - discountAmount, 0);
    const currentUnitPrice = Number.isFinite(computedResult.price) ? computedResult.price : linePrice;
    if (!Number.isFinite(currentUnitPrice) || currentUnitPrice >= cappedUnitPrice) {
        return computedResult;
    }
    const effectiveDiscountedQty = Number.isFinite(discountedQty) && discountedQty > 0
        ? Math.min(discountedQty, quantity)
        : quantity;
    if (effectiveDiscountedQty <= 0) {
        return computedResult;
    }
    const fullQty = Math.max(quantity - effectiveDiscountedQty, 0);
    const totalPrice = cappedUnitPrice * effectiveDiscountedQty + linePrice * fullQty;
    const result = Object.assign(Object.assign({}, computedResult), { price: cappedUnitPrice, totalPrice });
    if (!Number.isFinite(result.priceBefore)) {
        result.priceBefore = linePrice;
    }
    if (!Number.isFinite(result.totalPriceBefore)) {
        result.totalPriceBefore = linePrice * quantity;
    }
    if (Number.isFinite(vatRatePerMille)) {
        result.totalVat = calculateVat(vatRatePerMille, totalPrice);
    }
    return result;
};
const assignVoucherDiscountsForOrderLines = (topLevelOrderLines, options, orderTags) => {
    const voucherIdAndName = Array.isArray(orderTags) && extractValueFromOrderTags('Voucher', orderTags);
    const voucherId = voucherIdAndName ? voucherIdAndName.split(',').map((e) => e.trim())[1] : undefined;
    const existingVouchers = (options === null || options === void 0 ? void 0 : options.vouchers) || [];
    const vouchers = voucherId && !existingVouchers.includes(voucherId) ? existingVouchers.concat(voucherId) : existingVouchers;
    const optionsWithVouchers = vouchers === existingVouchers ? options : Object.assign(Object.assign({}, options), { vouchers });
    const recurse = (line, rootOrderLine) => {
        var _a, _b, _c, _d, _e;
        if (((_b = (_a = line.discount) === null || _a === void 0 ? void 0 : _a.voucherPattern) === null || _b === void 0 ? void 0 : _b.length) && !shouldApplyDiscount(line.discount, optionsWithVouchers)) {
            delete line.discount;
        }
        const applicableDiscounts = 'applicableDiscounts' in line ? line.applicableDiscounts : undefined;
        const isVoucherAppliedToCurrentLine = (_d = (_c = line.discount) === null || _c === void 0 ? void 0 : _c.voucherPattern) === null || _d === void 0 ? void 0 : _d.includes(voucherId);
        if (voucherIdAndName &&
            isVoucherAppliedToCurrentLine &&
            Array.isArray(applicableDiscounts) &&
            applicableDiscounts.length > 0) {
            const tempComputedPrice = Object.assign(Object.assign({}, line), { isMenu: rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.isMenu, discount: line.discount, discountQuantity: 0 });
            const bestApplicableDiscount = findBestApplicableDiscount(applicableDiscounts, optionsWithVouchers, tempComputedPrice, (rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.isMenu) || false);
            if (bestApplicableDiscount) {
                line.discount = bestApplicableDiscount;
            }
        }
        if ((_e = line.subLines) === null || _e === void 0 ? void 0 : _e.length) {
            line.subLines.forEach((subLine) => recurse(subLine, rootOrderLine || line));
        }
    };
    topLevelOrderLines.forEach((line) => {
        var _a, _b;
        if (((_b = (_a = line.discount) === null || _a === void 0 ? void 0 : _a.voucherPattern) === null || _b === void 0 ? void 0 : _b.length) && !shouldApplyDiscount(line.discount, optionsWithVouchers)) {
            delete line.discount;
        }
        recurse(line, line);
    });
};
export const applyDiscountsForOrderLines = (topLevelOrderLines, options = {}, orderTags = [], computedGlobalPriceRules, session) => {
    const sessionRef = session ||
        ensureOrderBuildSession(undefined, {
            cartProductsMap: options === null || options === void 0 ? void 0 : options.cartProductsMap,
            exceededPriceRules: options === null || options === void 0 ? void 0 : options.exceededPriceRules,
            isRootProductMenu: options === null || options === void 0 ? void 0 : options.isRootProductMenu,
        });
    const newOrderLines = [];
    const computedPricesMap = {};
    const isSunsetPlus = options.customerType === CUSTOMER_TYPES.PLUS_MEMBERS;
    const voucherIdAndName = Array.isArray(orderTags) && extractValueFromOrderTags('Voucher', orderTags);
    let usesPerTransactionToInt = 1;
    let voucherOnlinePosProductId = null;
    if (voucherIdAndName) {
        const voucherIdAndNameSplit = voucherIdAndName.split(',').map((e) => e.trim());
        usesPerTransactionToInt = parseInt(voucherIdAndNameSplit[3]);
        voucherOnlinePosProductId = parseInt(voucherIdAndNameSplit[4]);
    }
    let availableUsesPerTransaction = usesPerTransactionToInt;
    const spentDiscountQty = {};
    const menuFixedDiscountStats = new WeakMap();
    const shouldUseRootDiscount = (line, rootOrderLine, parentOrderLine) => {
        var _a, _b, _c, _d;
        return (isGiveawayCampaignDiscountOption((_a = rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.discount) === null || _a === void 0 ? void 0 : _a.options) ||
            (line.name.toLowerCase() === 'pant' && ((_c = (_b = rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.discount) === null || _b === void 0 ? void 0 : _b.options) === null || _c === void 0 ? void 0 : _c.pant_included)) ||
            ((rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.discount) && ((_d = parentOrderLine === null || parentOrderLine === void 0 ? void 0 : parentOrderLine.options) === null || _d === void 0 ? void 0 : _d.defaultSelected)));
    };
    const getMenuComponentDefaultPrice = (line, parentOrderLine) => {
        var _a;
        const includedInMenuPrice = (_a = parentOrderLine === null || parentOrderLine === void 0 ? void 0 : parentOrderLine.options) === null || _a === void 0 ? void 0 : _a.includedInMenuPrice;
        if (typeof includedInMenuPrice === 'number' && Number.isFinite(includedInMenuPrice)) {
            return includedInMenuPrice;
        }
        return typeof line.price === 'number' && Number.isFinite(line.price) ? line.price : 0;
    };
    const computeMenuFixedDiscountStats = (rootOrderLine) => {
        let totalDefaultPrice = 0;
        let totalUnits = 0;
        const walk = (node, parent) => {
            var _a;
            if ((_a = node.subLines) === null || _a === void 0 ? void 0 : _a.length) {
                node.subLines.forEach((subLine) => walk(subLine, node));
            }
            if (!parent)
                return;
            if (!shouldUseRootDiscount(node, rootOrderLine, parent))
                return;
            if (node.type !== 'Product')
                return;
            const quantity = typeof node.quantity === 'number' && Number.isFinite(node.quantity) ? node.quantity : 1;
            if (quantity > 0) {
                totalUnits += quantity;
            }
            const defaultPrice = getMenuComponentDefaultPrice(node, parent);
            if (defaultPrice > 0) {
                totalDefaultPrice += defaultPrice;
            }
        };
        walk(rootOrderLine);
        return { totalDefaultPrice, totalUnits };
    };
    const getMenuFixedDiscountStats = (rootOrderLine) => {
        if (!(rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.isMenu))
            return { totalDefaultPrice: 0, totalUnits: 0 };
        const rootDiscount = rootOrderLine.discount;
        if (!rootDiscount || rootDiscount.discountType !== 'simpleDiscountFixedAmount') {
            return { totalDefaultPrice: 0, totalUnits: 0 };
        }
        const cached = menuFixedDiscountStats.get(rootOrderLine);
        if (cached)
            return cached;
        const stats = computeMenuFixedDiscountStats(rootOrderLine);
        menuFixedDiscountStats.set(rootOrderLine, stats);
        return stats;
    };
    const shouldSplitMenuFixedDiscount = (discount, rootOrderLine, hasRootDiscount) => {
        var _a;
        if (!discount || !hasRootDiscount || !(rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.isMenu)) {
            return false;
        }
        if (discount.discountType !== 'simpleDiscountFixedAmount') {
            return false;
        }
        const rootDiscountId = (_a = rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.discount) === null || _a === void 0 ? void 0 : _a.discountId;
        if (rootDiscountId !== undefined && rootDiscountId !== null && discount.discountId !== rootDiscountId) {
            return false;
        }
        return true;
    };
    const splitMenuFixedDiscount = (discount, line, rootOrderLine, parentOrderLine, hasRootDiscount) => {
        if (!shouldSplitMenuFixedDiscount(discount, rootOrderLine, hasRootDiscount)) {
            return discount;
        }
        const stats = getMenuFixedDiscountStats(rootOrderLine);
        const defaultPrice = getMenuComponentDefaultPrice(line, parentOrderLine);
        let perUnitDiscountValue;
        if (stats.totalDefaultPrice > 0) {
            perUnitDiscountValue = discount.discountValue * (defaultPrice / stats.totalDefaultPrice);
        }
        else if (stats.totalUnits > 0) {
            perUnitDiscountValue = discount.discountValue / stats.totalUnits;
        }
        if (!Number.isFinite(perUnitDiscountValue)) {
            return discount;
        }
        return Object.assign(Object.assign({}, discount), { discountValue: perUnitDiscountValue });
    };
    const recurse = (line, accumulator, level = 0, rootOrderLine, parentOrderLine) => {
        var _a, _b, _c, _d, _e, _f;
        const applicableDiscounts = 'applicableDiscounts' in line ? line.applicableDiscounts : undefined;
        let newLine = Object.assign(Object.assign({}, line), { subLines: [] });
        const quantity = 'quantity' in line ? line.quantity : 1;
        const hasRootDiscount = shouldUseRootDiscount(line, rootOrderLine, parentOrderLine);
        const discount = (() => {
            var _a, _b, _c, _d;
            if (!rootOrderLine) {
                return undefined;
            }
            if ((usesPerTransactionToInt === 0 || availableUsesPerTransaction >= 0) &&
                (line.name.toLowerCase() !== 'pant' || ((_b = (_a = parentOrderLine === null || parentOrderLine === void 0 ? void 0 : parentOrderLine.discount) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.pant_included))) {
                const rootOrLineOrderDiscount = hasRootDiscount ? rootOrderLine.discount : line.discount;
                const [, voucherId] = voucherIdAndName.split(',').map((e) => e.trim());
                const isVoucherAssignedToDiscount = ((rootOrLineOrderDiscount === null || rootOrLineOrderDiscount === void 0 ? void 0 : rootOrLineOrderDiscount.voucherPattern) || []).includes(voucherId);
                if (isVoucherAssignedToDiscount && availableUsesPerTransaction === 0) {
                    return undefined;
                }
                const shouldApply = shouldApplyDiscount(rootOrLineOrderDiscount, options);
                if ((_c = rootOrLineOrderDiscount === null || rootOrLineOrderDiscount === void 0 ? void 0 : rootOrLineOrderDiscount.options) === null || _c === void 0 ? void 0 : _c.context) {
                    return rootOrLineOrderDiscount;
                }
                const discountScope = ((_d = rootOrLineOrderDiscount === null || rootOrLineOrderDiscount === void 0 ? void 0 : rootOrLineOrderDiscount.options) === null || _d === void 0 ? void 0 : _d.discountScope) || DiscountScope.All;
                const discountScopes = rootOrderLine.isMenu
                    ? [DiscountScope.Group, DiscountScope.All]
                    : [DiscountScope.Single, DiscountScope.All];
                const isDiscountApplicable = discountScopes.includes(discountScope);
                if (isDiscountApplicable && shouldApply) {
                    return rootOrLineOrderDiscount;
                }
            }
            return undefined;
        })();
        const adjustedDiscount = splitMenuFixedDiscount(discount, line, rootOrderLine, parentOrderLine, hasRootDiscount);
        const linePrice = newLine === null || newLine === void 0 ? void 0 : newLine.price;
        const computedPrices = Object.assign(Object.assign(Object.assign(Object.assign({}, line), { isMenu: rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.isMenu, discount: adjustedDiscount, discountQuantity: 0 }), (linePrice && { price: linePrice })), ('quantity' in line ? { quantity: line['quantity'] } : {}));
        if (line.subLines) {
            line.subLines.forEach((subLine) => recurse(subLine, newLine.subLines, level + 1, rootOrderLine, line));
        }
        if (voucherIdAndName && (usesPerTransactionToInt === 0 || availableUsesPerTransaction > 0)) {
            const bestApplicableDiscount = findBestApplicableDiscount(applicableDiscounts, options, computedPrices, (rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.isMenu) || false);
            if (bestApplicableDiscount) {
                computedPrices.discount = splitMenuFixedDiscount(bestApplicableDiscount, line, rootOrderLine, parentOrderLine, hasRootDiscount);
                computedPrices.discountQuantity = quantity;
                const [voucherCode, voucherId, voucherName] = voucherIdAndName.split(',').map((e) => e.trim());
                const voucherSubline = {
                    name: 'VOUCHER',
                    subLines: [],
                    type: 'Voucher',
                    tags: [`Voucher.${voucherCode}.${voucherId}.${voucherName}`],
                    generation: DomainCoreGeneration.Generation5,
                    values: [
                        {
                            type: 'Voucher',
                            voucherId,
                            voucherName,
                            voucherCode,
                            usesPerTransaction: usesPerTransactionToInt,
                            onlinePosProductId: voucherOnlinePosProductId || ((_b = (_a = computedPrices.discount) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.onlinePosId),
                        },
                    ],
                };
                const isVoucherAssignedToPriceRule = ((bestApplicableDiscount === null || bestApplicableDiscount === void 0 ? void 0 : bestApplicableDiscount.voucherPattern) || []).includes(voucherId);
                if (isVoucherAssignedToPriceRule && usesPerTransactionToInt !== 0) {
                    if (quantity > availableUsesPerTransaction) {
                        computedPrices.discountQuantity = availableUsesPerTransaction;
                    }
                    availableUsesPerTransaction -= quantity;
                }
                const sumTotalPriceRecursive = (currentLine) => {
                    let totalPrice = currentLine.totalPrice || 0;
                    if (currentLine.subLines && currentLine.subLines.length > 0) {
                        for (const subLine of currentLine.subLines) {
                            if (!subLine.productId)
                                continue;
                            totalPrice += sumTotalPriceRecursive(subLine);
                        }
                    }
                    return totalPrice;
                };
                const totalPriceWithSubLines = sumTotalPriceRecursive(newLine);
                if (isVoucherAssignedToPriceRule &&
                    ((rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.isMenu) ||
                        (((_c = computedPrices.discount) === null || _c === void 0 ? void 0 : _c.discountType) === 'simpleDiscountFixedAmount' &&
                            totalPriceWithSubLines >= computedPrices.discount.discountValue) ||
                        (((_d = computedPrices.discount) === null || _d === void 0 ? void 0 : _d.discountType) === 'percentDiscount' &&
                            totalPriceWithSubLines >=
                                (totalPriceWithSubLines * computedPrices.discount.discountValue) / 100))) {
                    newLine.subLines.push(voucherSubline);
                }
            }
        }
        if (options.enableProductBestPriceInCatalogAndCart && isSunsetPlus) {
            if (isProductSpecificDiscount(computedPrices.discount) && (computedGlobalPriceRules === null || computedGlobalPriceRules === void 0 ? void 0 : computedGlobalPriceRules[0])) {
                if (rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.productSelectionId) {
                    computedPricesMap[rootOrderLine.productSelectionId] = Object.assign(Object.assign({}, (computedPricesMap[rootOrderLine.productSelectionId] || {})), { [newLine.productId]: computedPrices });
                }
            }
        }
        const currentDiscount = computedPrices.discount;
        const effectiveDiscountQtyLimit = getEffectiveQuantityDiscountLimit(currentDiscount, sessionRef);
        const productIdForDiscount = hasRootDiscount ? rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.productId : newLine.productId;
        if (effectiveDiscountQtyLimit && (currentDiscount === null || currentDiscount === void 0 ? void 0 : currentDiscount.productPattern.includes(productIdForDiscount))) {
            const remainingDiscountQuantity = effectiveDiscountQtyLimit -
                (rootOrderLine &&
                    ((_f = (_e = sessionRef.cartProductsMap[rootOrderLine.productId]) === null || _e === void 0 ? void 0 : _e.selections) === null || _f === void 0 ? void 0 : _f[rootOrderLine.productSelectionId])
                    ? spentDiscountQty[currentDiscount.discountId] || 0
                    : 0);
            if (remainingDiscountQuantity > 0) {
                let computedResult = PriceMgr.compute(Object.assign(Object.assign(Object.assign({}, computedPrices), { discountQuantity: remainingDiscountQuantity > -1 ? remainingDiscountQuantity : 0, effectiveDiscountQtyLimit }), ('vatRatePerMille' in line && { vatRatePerMille: line.vatRatePerMille })));
                computedResult = applyMenuSubProductDiscountCap({
                    computedResult,
                    linePrice: linePrice !== null && linePrice !== void 0 ? linePrice : newLine.price,
                    quantity,
                    discount: currentDiscount,
                    rootOrderLine,
                    parentOrderLine,
                    productId: newLine.productId,
                    discountedQty: remainingDiscountQuantity,
                    vatRatePerMille: 'vatRatePerMille' in line ? line.vatRatePerMille : undefined,
                });
                newLine = Object.assign(Object.assign({}, newLine), computedResult);
                if (computedResult.discountQuantity !== undefined) {
                    newLine.discountQuantity = computedResult.discountQuantity;
                }
                if (currentDiscount) {
                    const discountId = currentDiscount.discountId;
                    if (!spentDiscountQty[discountId]) {
                        spentDiscountQty[discountId] = 0;
                    }
                    if (((rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.discount) && level === 0) || !(rootOrderLine === null || rootOrderLine === void 0 ? void 0 : rootOrderLine.discount)) {
                        spentDiscountQty[discountId] = (spentDiscountQty[discountId] || 0) + (computedPrices.quantity || 0);
                    }
                }
            }
        }
        else {
            let computedResult = PriceMgr.compute(computedPrices);
            computedResult = applyMenuSubProductDiscountCap({
                computedResult,
                linePrice: linePrice !== null && linePrice !== void 0 ? linePrice : newLine.price,
                quantity,
                discount: currentDiscount,
                rootOrderLine,
                parentOrderLine,
                productId: newLine.productId,
                discountedQty: computedPrices.discountQuantity,
                vatRatePerMille: 'vatRatePerMille' in line ? line.vatRatePerMille : undefined,
            });
            newLine = Object.assign(Object.assign({}, newLine), computedResult);
            if (computedResult.discountQuantity !== undefined) {
                newLine.discountQuantity = computedResult.discountQuantity;
            }
        }
        accumulator.push(Object.assign(Object.assign({}, newLine), (currentDiscount && { discount: currentDiscount })));
    };
    topLevelOrderLines.forEach((line) => {
        recurse(line, newOrderLines, 0, line, line);
    });
    if (isSunsetPlus && Object.keys(computedPricesMap).length && (computedGlobalPriceRules === null || computedGlobalPriceRules === void 0 ? void 0 : computedGlobalPriceRules.length)) {
        const globalRule = computedGlobalPriceRules[0];
        let cartSpendings = OrderLineOptionsGetter.dailySpending(options);
        for (let i = 0; i < newOrderLines.length; i++) {
            const item = newOrderLines[i];
            const itemPrice = getOrderLinePrice(item, true);
            const computedPrice = item.productSelectionId ? computedPricesMap[item.productSelectionId] : undefined;
            if (!computedPrice) {
                cartSpendings += itemPrice;
                continue;
            }
            const discountValue = getOrderLineDiscountValue(item);
            const { discountMax, discountPct } = globalRule.options;
            let currentlyRemainingPLUSdiscount = Math.max((discountMax - cartSpendings) * discountPct, 0);
            const plusDiscount = itemPrice * discountPct;
            currentlyRemainingPLUSdiscount = Math.min(currentlyRemainingPLUSdiscount, plusDiscount);
            if (currentlyRemainingPLUSdiscount > discountValue) {
                const traverseOrderLine = (orderLine) => {
                    if (!orderLine)
                        return;
                    if (isProductSpecificDiscount(orderLine.discount)) {
                        const computed = computedPrice[orderLine.productId];
                        if (computed) {
                            delete computed.discount;
                            Object.assign(orderLine, PriceMgr.compute(computed));
                        }
                    }
                    if (orderLine.subLines) {
                        orderLine.subLines.forEach(traverseOrderLine);
                    }
                };
                traverseOrderLine(item);
                cartSpendings += getOrderLinePrice(item, true);
            }
            else {
                cartSpendings += itemPrice - discountValue;
                orderLineSetSkipInTotalRabat(item);
            }
        }
    }
    return newOrderLines;
};
export function finalizeOrderLines(orderLines) {
    OrderLineMappingG5Helper.accumulatePrices(orderLines);
    OrderLineMappingG5Helper.aggregateQuantityTopDown(orderLines);
    return orderLines;
}
const collectAvailableCombinatorRules = (orderLines, options, discountTypeDisabled, globalRules) => {
    const rulesReducer = (sentOrderLines) => sentOrderLines.reduce((acc, ol) => {
        if (ol.type === 'Product') {
            if (ol.discount &&
                ol.discount.discountType === 'familyDiscount' &&
                !discountTypeDisabled.includes(ol.discount.discountType)) {
                if (!globalRules[0] || ol.discount.options.priority >= globalRules[0].options.priority) {
                    const combinatorDiscounts = ol.discount;
                    if (shouldApplyDiscount(combinatorDiscounts, options)) {
                        return Object.assign(Object.assign({}, acc), { [combinatorDiscounts.options.label]: combinatorDiscounts });
                    }
                }
            }
        }
        if (ol.subLines.length > 0) {
            return Object.assign(Object.assign({}, acc), rulesReducer(ol.subLines));
        }
        return acc;
    }, {});
    return rulesReducer(orderLines);
};
const applyPricingPipeline = (orderLines, productCatalog, options, orderTags, computedGlobalPriceRules, combinatorRulesToApply, availableCombinatorRules, session) => {
    const additionalLines = createAdditionalLines(orderTags, options);
    const applyAdditionalLines = (currentOrderLine, linesToAdd) => {
        return linesToAdd.reduce((acc, curr) => {
            return acc.concat([curr]);
        }, [...currentOrderLine]);
    };
    const generateOrderLineWithDiscount = (fullOrderLine, discountLineOrderLine) => {
        OrderLineMappingG5Helper.fixPricesForProductsWithFreeAdditionsG5(fullOrderLine);
        const [freeDiscountLine, discountLine] = generateDiscountLine(discountLineOrderLine, orderTags, productCatalog, computedGlobalPriceRules, options);
        return finalizeOrderLines(applyAdditionalLines(fullOrderLine, additionalLines)
            .concat(freeDiscountLine.type === 'FreeMeal' && freeDiscountLine.totalPrice !== 0 ? freeDiscountLine : null, discountLine.type === 'Discount' &&
            (discountLine.totalPrice !== 0 || discountLine.options.hasReachedDailyLimit)
            ? discountLine
            : null)
            .filter(Boolean));
    };
    const applyCombinatorLines = (rulesToApply, inputOrderLines, opts, tags, buildSession) => {
        const [combinatorLines, unappliedOrderLines] = Object.keys(availableCombinatorRules).length === 0
            ? [[], inputOrderLines]
            : applyCombinatorDiscounts(inputOrderLines, rulesToApply);
        const orderLinesEffectivePricesCombinator = applyDiscountsForOrderLines(combinatorLines, opts, tags, computedGlobalPriceRules, buildSession);
        const orderLinesEffectivePricesUnapplied = applyDiscountsForOrderLines(unappliedOrderLines, opts, tags, computedGlobalPriceRules, buildSession);
        const topLevelOL = [
            ...orderLinesEffectivePricesCombinator,
            ...orderLinesEffectivePricesUnapplied,
        ];
        return generateOrderLineWithDiscount(topLevelOL, orderLinesEffectivePricesUnapplied);
    };
    const shouldApplyGlobalRuleOnly = computedGlobalPriceRules[0] &&
        computedGlobalPriceRules[0].options.priority >=
            (combinatorRulesToApply[0] ? combinatorRulesToApply[0].options.priority : 0);
    if (shouldApplyGlobalRuleOnly) {
        const orderLinesEffectivePrices = applyDiscountsForOrderLines(orderLines, options, orderTags, computedGlobalPriceRules, session);
        const orderLineWithDiscounts = generateOrderLineWithDiscount(orderLinesEffectivePrices, orderLinesEffectivePrices);
        const discountLine = orderLineWithDiscounts.find((ol) => ol.type === 'Discount');
        if (discountLine && discountLine.options.hasReachedDailyLimit) {
            return applyCombinatorLines(combinatorRulesToApply, orderLines, options, orderTags, session);
        }
        return orderLineWithDiscounts;
    }
    return applyCombinatorLines(combinatorRulesToApply, orderLines, options, orderTags, session);
};
export const runOrderPricingPipeline = ({ productCatalog, orderLines, options, orderTags, includedPriceDisabled, discountTypeDisabled, session, }) => {
    assignVirtualIds(orderLines);
    const { computedGlobalPriceRules, priceRuleAcc } = evaluatePriceRules({
        priceRules: productCatalog.priceRules,
        productCatalog,
        options,
        orderTags,
        session,
    });
    injectIncludedPrices(orderLines, includedPriceDisabled);
    assignVoucherDiscountsForOrderLines(orderLines, options, orderTags);
    const availableCombinatorRules = collectAvailableCombinatorRules(orderLines, options, discountTypeDisabled, priceRuleAcc.global);
    const combinatorRulesToApply = Object.values(availableCombinatorRules).sort((rule1, rule2) => rule2.options.priority - rule1.options.priority);
    return applyPricingPipeline(orderLines, productCatalog, options, orderTags, computedGlobalPriceRules, combinatorRulesToApply, availableCombinatorRules, session);
};
export const OrderPricingPipeline = Object.freeze({
    runOrderPricingPipeline,
    finalizeOrderLines,
});
